import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { buildSeriesShows } from "../series";
import { categoryKey, videoCategories } from "../categories";
import "./RemotePage.css";

const REMOTE_STORAGE_KEY = "cs_remote_session";
const MOVIE_PAGE_SIZE = 36;

const LANG_LABELS = {
  spa: "Español Latino", es: "Español", esp: "Español", lat: "Español Latino",
  eng: "Inglés", en: "Inglés", por: "Portugués", pt: "Portugués",
  off: "Desactivados",
};

function langLabel(id, fallback) {
  const k = String(id || "").toLowerCase();
  return LANG_LABELS[k] || fallback || id || "—";
}

function useInfiniteScroll(enabled, hasMore, onLoadMore) {
  const ref = useRef(null);
  useEffect(() => {
    if (!enabled || !hasMore) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) onLoadMore(); },
      { rootMargin: "480px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [enabled, hasMore, onLoadMore]);
  return ref;
}

const NAV = [
  { id: "home", label: "Dashboard", icon: "home" },
  { id: "movies", label: "Películas", icon: "movies" },
  { id: "series", label: "Series", icon: "series" },
  { id: "anime", label: "Anime", icon: "anime" },
  { id: "iptv", label: "IPTV", icon: "iptv" },
];

const TITLES = {
  home: "Dashboard",
  movies: "Películas",
  series: "Series",
  anime: "Anime",
  iptv: "IPTV en vivo",
};

function NavSvg({ name }) {
  const p = { width: 22, height: 22, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "home":
      return (<svg {...p}><path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1z" /></svg>);
    case "movies":
      return (<svg {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M7 5v14M17 5v14" /></svg>);
    case "series":
      return (<svg {...p}><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></svg>);
    case "anime":
      return (<svg {...p}><path d="M12 3l1.4 4.2L18 9l-4.6 1.6L12 15l-1.4-4.4L6 9l4.6-1.8z" /></svg>);
    case "iptv":
      return (<svg {...p}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="m10 9 5 3-5 3z" fill="currentColor" stroke="none" /></svg>);
    case "search":
      return (<svg {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>);
    case "rewind":
      return (<svg {...p}><path d="M11 7 6 12l5 5V7zM18 7l-5 5 5 5V7z" fill="currentColor" stroke="none" /></svg>);
    case "forward":
      return (<svg {...p}><path d="M13 7v10l5-5-5-5zM6 7v10l5-5-5-5z" fill="currentColor" stroke="none" /></svg>);
    case "play":
      return (<svg {...p}><path d="M9 7v10l8-5-8-5z" fill="currentColor" stroke="none" /></svg>);
    case "pause":
      return (<svg {...p}><path d="M8 7h3v10H8zM13 7h3v10h-3z" fill="currentColor" stroke="none" /></svg>);
    case "stop":
      return (<svg {...p}><rect x="7" y="7" width="10" height="10" rx="1" fill="currentColor" stroke="none" /></svg>);
    case "back":
      return (<svg {...p}><path d="M15 6 9 12l6 6" /></svg>);
    case "audio":
      return (<svg {...p}><path d="M12 4a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V7a3 3 0 0 0-3-3z" /><path d="M16 10a4 4 0 0 1-4 4" /><path d="M18 8a6 6 0 0 1-6 6" /></svg>);
    case "subs":
      return (<svg {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M7 10h4M7 14h7" /></svg>);
    default:
      return null;
  }
}

function loadStoredSession() {
  try {
    const raw = localStorage.getItem(REMOTE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStoredSession(data) {
  if (!data) localStorage.removeItem(REMOTE_STORAGE_KEY);
  else localStorage.setItem(REMOTE_STORAGE_KEY, JSON.stringify(data));
}

function clearRemoteSession(setSession, extras = {}) {
  saveStoredSession(null);
  setSession(null);
  if (extras.setTvState) extras.setTvState(null);
  if (extras.setTvOnline) extras.setTvOnline(false);
  if (extras.setSelectedShow) extras.setSelectedShow(null);
  if (extras.setGlobalQ) extras.setGlobalQ("");
  if (extras.setPinInput) extras.setPinInput("");
}

function posterUrl(item) {
  return item?.cover_url || item?.series_poster_url || item?.season_poster_url || item?.poster_url || item?.logo || "";
}

function formatTime(sec) {
  const t = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

function seasonOf(ep) {
  const n = ep?.season_number;
  if (n == null || Number.isNaN(Number(n))) return 1;
  return Number(n);
}

function episodesForSeason(show, season) {
  return (show?.items || [])
    .filter((ep) => seasonOf(ep) === Number(season))
    .sort((a, b) => (a.episode_number ?? 0) - (b.episode_number ?? 0) || String(a.title).localeCompare(String(b.title), "es"));
}

function episodeLabel(ep, seriesTitle, index) {
  const number = ep.episode_number != null && !Number.isNaN(Number(ep.episode_number)) ? Number(ep.episode_number) : index + 1;
  let name = String(ep.title || "").trim();
  if (seriesTitle) {
    const re = new RegExp(`^${String(seriesTitle).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*[·:\\-]\\s*`, "i");
    name = name.replace(re, "").trim();
  }
  if (!name || name.toLowerCase() === seriesTitle?.toLowerCase()) return { number, title: `Capítulo ${number}` };
  return { number, title: name };
}

function formatDuration(sec) {
  const total = Math.max(0, Math.round(Number(sec) || 0));
  if (!total) return "";
  return `${Math.floor(total / 60)} min`;
}

function PosterCard({ item, title, subtitle, onClick, badge, index = 0 }) {
  const img = posterUrl(item);
  return (
    <article className="rcx-card rcx-stagger" style={{ "--rcx-i": index }}>
      <button type="button" className="rcx-card-btn" onClick={onClick} aria-label={title}>
        {img ? <img src={img} alt="" loading="lazy" decoding="async" /> : (
          <div className="rcx-card-fallback">{(title || "?").slice(0, 1)}</div>
        )}
        {badge ? <span className="rcx-badge-live">{badge}</span> : null}
        <span className="rcx-card-shine" />
      </button>
      <div className="rcx-card-meta">
        <strong>{title}</strong>
        {subtitle ? <span>{subtitle}</span> : null}
      </div>
    </article>
  );
}

function RemoteTransport({ playing, title, cmdBusy, onSeek, onSeekDelta, onTogglePlay, onBack, onStop, onSetAudio, onSetSubtitle }) {
  const [dragging, setDragging] = useState(false);
  const [localTime, setLocalTime] = useState(0);
  const isLive = !!playing?.iptv;
  const duration = Math.max(0, Number(playing?.duration) || 0);
  const current = dragging ? localTime : Number(playing?.currentTime) || 0;
  const pct = duration > 0 ? Math.min(100, (current / duration) * 100) : 0;
  const audioTracks = playing?.audio_tracks || [];
  const subTracks = playing?.subtitle_tracks || [];
  const showTracks = !isLive && (audioTracks.length > 0 || subTracks.length > 0);

  useEffect(() => {
    if (!dragging) setLocalTime(Number(playing?.currentTime) || 0);
  }, [playing?.currentTime, dragging]);

  if (!playing) return null;

  return (
    <footer className="rcx-transport rcx-transport-rich">
      <div className="rcx-transport-glow" />
      <div className="rcx-transport-inner">
        <div className="rcx-transport-head">
          <div className="rcx-transport-info">
            <strong>{title || "Reproduciendo en TV"}</strong>
            <span>{isLive ? "● EN VIVO" : `${formatTime(current)} / ${formatTime(duration)}`}</span>
          </div>
          {playing.paused ? <span className="rcx-transport-badge paused">Pausado</span> : <span className="rcx-transport-badge playing">En curso</span>}
        </div>

        {showTracks ? (
          <div className="rcx-track-row">
            {audioTracks.length > 0 ? (
              <label className="rcx-track-pick">
                <span className="rcx-track-pick-label"><NavSvg name="audio" /> Audio</span>
                <select
                  className="rcx-track-select"
                  value={playing.audio_id || audioTracks[0]?.id || ""}
                  disabled={cmdBusy}
                  onChange={(e) => onSetAudio(e.target.value)}
                >
                  {audioTracks.map((t) => (
                    <option key={t.id} value={t.id}>{langLabel(t.id, t.label)}</option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="rcx-track-pick">
              <span className="rcx-track-pick-label"><NavSvg name="subs" /> Subtítulos</span>
              <select
                className="rcx-track-select"
                value={playing.subtitle_id || "off"}
                disabled={cmdBusy}
                onChange={(e) => onSetSubtitle(e.target.value)}
              >
                <option value="off">Desactivados</option>
                {subTracks.map((t) => (
                  <option key={t.id} value={t.id}>{langLabel(t.id, t.label)}</option>
                ))}
              </select>
            </label>
          </div>
        ) : null}

        {!isLive && duration > 0 ? (
          <div className="rcx-seek-wrap">
            <input
              type="range"
              className="rcx-seek"
              min={0}
              max={duration}
              step={1}
              value={Math.min(current, duration)}
              disabled={cmdBusy}
              onPointerDown={() => setDragging(true)}
              onPointerUp={() => setDragging(false)}
              onChange={(e) => {
                const v = Number(e.target.value);
                setLocalTime(v);
                onSeek(v);
              }}
            />
            <div className="rcx-seek-track">
              <div className="rcx-seek-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="rcx-seek-labels">
              <span>{formatTime(current)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        ) : (
          <div className="rcx-seek-live">Transmisión en directo — usa los controles para pausar o cambiar canal</div>
        )}

        <div className="rcx-transport-controls">
          <button type="button" className="rcx-ctl" disabled={cmdBusy || isLive} onClick={() => onSeekDelta(-30)} title="−30s">
            <NavSvg name="rewind" /><span>30</span>
          </button>
          <button type="button" className="rcx-ctl" disabled={cmdBusy || isLive} onClick={() => onSeekDelta(-10)} title="−10s">
            <NavSvg name="rewind" /><span>10</span>
          </button>
          <button type="button" className="rcx-ctl rcx-ctl-main" disabled={cmdBusy} onClick={onTogglePlay} title={playing.paused ? "Reproducir" : "Pausar"}>
            <NavSvg name={playing.paused ? "play" : "pause"} />
          </button>
          <button type="button" className="rcx-ctl" disabled={cmdBusy || isLive} onClick={() => onSeekDelta(10)} title="+10s">
            <NavSvg name="forward" /><span>10</span>
          </button>
          <button type="button" className="rcx-ctl" disabled={cmdBusy || isLive} onClick={() => onSeekDelta(30)} title="+30s">
            <NavSvg name="forward" /><span>30</span>
          </button>
          <button type="button" className="rcx-ctl" disabled={cmdBusy} onClick={onBack} title="Salir del reproductor">
            <NavSvg name="back" />
          </button>
          <button type="button" className="rcx-ctl rcx-ctl-danger" disabled={cmdBusy} onClick={onStop} title="Detener">
            <NavSvg name="stop" />
          </button>
        </div>
      </div>
    </footer>
  );
}

function ShowControlView({ show, onBack, onPlayEpisode, cmdBusy, animKey }) {
  const [season, setSeason] = useState(show.seasons?.[0] ?? 1);
  const episodes = useMemo(() => episodesForSeason(show, season), [show, season]);

  return (
    <div key={animKey} className="rcx-show-view rcx-page-enter">
      <div className="rcx-show-hero rcx-stagger" style={{ "--rcx-i": 0 }}>
        {show.backdrop_url ? <div className="rcx-show-hero-bg" style={{ backgroundImage: `url(${show.backdrop_url})` }} /> : null}
        <div className="rcx-show-hero-content">
          <div className="rcx-show-poster">
            {show.cover_url ? <img src={show.cover_url} alt="" /> : <div className="rcx-card-fallback">{show.title.slice(0, 1)}</div>}
          </div>
          <div className="rcx-show-info">
            <h2>{show.title}</h2>
            <p>{show.seasonCount} temp. · {show.count} capítulos</p>
            <p className="rcx-local-hint">Elige temporada y capítulo · la TV no cambia hasta pulsar ▶</p>
            <div className="rcx-show-actions">
              <button type="button" className="rcx-btn rcx-btn-ghost rcx-btn-sm" onClick={onBack}>← Volver</button>
              {episodes[0] ? (
                <button type="button" className="rcx-btn rcx-btn-primary rcx-btn-sm" disabled={cmdBusy} onClick={() => onPlayEpisode(episodes[0].id)}>
                  ▶ Reproducir T{season} E{episodes[0].episode_number ?? 1}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="rcx-panel rcx-stagger" style={{ "--rcx-i": 1 }}>
        <div className="rcx-panel-head"><h2>Temporada</h2></div>
        <div className="rcx-seasons">
          {(show.seasons || [1]).map((s) => (
            <button key={s} type="button" className={`rcx-season-btn ${season === s ? "is-active" : ""}`} onClick={() => setSeason(s)}>
              T{s}
            </button>
          ))}
        </div>
        <div className="rcx-episodes">
          {episodes.map((ep, i) => {
            const label = episodeLabel(ep, show.title, i);
            const thumb = ep.cover_url || show.seasonPosters?.[season] || show.cover_url;
            return (
              <div key={ep.id} className="rcx-ep-row rcx-stagger" style={{ "--rcx-i": i }}>
                <div className="rcx-ep-num">{label.number}</div>
                <div className="rcx-ep-thumb">{thumb ? <img src={thumb} alt="" loading="lazy" /> : null}</div>
                <div className="rcx-ep-info">
                  <strong>{label.title}</strong>
                  <span>{[ep.category, formatDuration(ep.duration_seconds)].filter(Boolean).join(" · ") || "Listo para TV"}</span>
                </div>
                <button type="button" className="rcx-btn rcx-btn-primary rcx-btn-sm" disabled={cmdBusy} onClick={() => onPlayEpisode(ep.id)}>▶ TV</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function RemotePage() {
  const { token } = useAuth();
  const [session, setSession] = useState(() => loadStoredSession());
  const [pinInput, setPinInput] = useState("");
  const [pairBusy, setPairBusy] = useState(false);
  const [pairError, setPairError] = useState("");
  const [tab, setTab] = useState("home");
  const [tabAnimKey, setTabAnimKey] = useState(0);
  const [selectedShow, setSelectedShow] = useState(null);
  const [movieCategory, setMovieCategory] = useState("");
  const [globalQ, setGlobalQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [searchChannels, setSearchChannels] = useState([]);
  const [videos, setVideos] = useState([]);
  const [recent, setRecent] = useState([]);
  const [iptvBrowse, setIptvBrowse] = useState(null);
  const [iptvChannels, setIptvChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tvState, setTvState] = useState(null);
  const [tvOnline, setTvOnline] = useState(false);
  const [cmdBusy, setCmdBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [movieLimit, setMovieLimit] = useState(MOVIE_PAGE_SIZE);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(globalQ.trim()), 350);
    return () => clearTimeout(t);
  }, [globalQ]);

  const sendCommand = useCallback(
    async (action, payload = {}, opts = {}) => {
      if (!session?.session_id || !token) {
        setStatusMsg("Empareja la TV primero");
        return;
      }
      setCmdBusy(true);
      try {
        await api("/api/remote/command", {
          method: "POST",
          token,
          body: { session_id: session.session_id, action, payload },
        });
        const labels = {
          play_video: "Reproduciendo en la TV",
          play_iptv: "Canal en la TV",
          pause: "Pausa",
          play: "Reproduciendo",
          stop: "Detenido",
          back: "Salió del reproductor",
          seek: "Posición actualizada",
          set_audio: "Idioma de audio actualizado",
          set_subtitle: "Subtítulos actualizados",
        };
        if (!opts.silent) {
          setStatusMsg(opts.message || labels[action] || "Listo");
          setTimeout(() => setStatusMsg(""), 1800);
        }
      } catch (err) {
        if (err.status === 404 || err.status === 403) {
          clearRemoteSession(setSession, { setTvState, setTvOnline, setSelectedShow, setGlobalQ, setPinInput });
          setStatusMsg("Sesión inválida. Ingresa el PIN de la TV.");
          return;
        }
        setStatusMsg(err.message || "Error al enviar");
      } finally {
        setCmdBusy(false);
      }
    },
    [session?.session_id, token]
  );

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api("/api/videos", { token }),
      api("/api/recently-watched?limit=16", { token }).catch(() => ({ videos: [] })),
      api("/api/iptv/browse", { token }).catch(() => null),
    ]).then(([allVideos, recentData, browse]) => {
      if (cancelled) return;
      setVideos((Array.isArray(allVideos) ? allVideos : []).filter((v) => v.status === "ready"));
      setRecent((recentData && recentData.videos) || []);
      setIptvBrowse(browse);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!token || tab !== "iptv" || debouncedQ.length >= 2) return undefined;
    let cancelled = false;
    api("/api/iptv/channels?limit=48&offset=0", { token })
      .then((data) => { if (!cancelled) setIptvChannels((data && data.channels) || []); })
      .catch(() => { if (!cancelled) setIptvChannels([]); });
    return () => { cancelled = true; };
  }, [token, tab, debouncedQ]);

  useEffect(() => {
    if (!token || debouncedQ.length < 2) {
      setSearchChannels([]);
      return undefined;
    }
    let cancelled = false;
    api(`/api/iptv/channels?limit=36&q=${encodeURIComponent(debouncedQ)}`, { token })
      .then((data) => { if (!cancelled) setSearchChannels((data && data.channels) || []); })
      .catch(() => { if (!cancelled) setSearchChannels([]); });
    return () => { cancelled = true; };
  }, [token, debouncedQ]);

  useEffect(() => {
    if (!token || !session?.session_id) return undefined;
    let cancelled = false;
    const poll = () => {
      api(`/api/remote/state?session_id=${encodeURIComponent(session.session_id)}`, { token })
        .then((data) => {
          if (cancelled) return;
          setTvOnline(!!data.online);
          setTvState(data.state || null);
        })
        .catch((err) => {
          if (cancelled) return;
          if (err.status === 404 || err.status === 403) {
            clearRemoteSession(setSession, { setTvState, setTvOnline, setSelectedShow, setGlobalQ });
            setStatusMsg("Sesión cerrada. Ingresa el PIN de la TV.");
            return;
          }
          setTvOnline(false);
          setTvState(null);
        });
    };
    poll();
    const id = setInterval(poll, 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token, session?.session_id]);

  async function onDisconnect() {
    const sessionId = session?.session_id;
    clearRemoteSession(setSession, { setTvState, setTvOnline, setSelectedShow, setGlobalQ, setPinInput });
    setStatusMsg("Desconectado. Mira el PIN en la TV e ingrésalo abajo.");
    if (!sessionId || !token) return;
    try {
      await api("/api/remote/unpair", {
        method: "POST",
        token,
        body: { session_id: sessionId },
      });
    } catch (_) {
      /* La sesión local ya se limpió; la TV renovará PIN en el próximo heartbeat */
    }
  }

  const movies = useMemo(() => videos.filter((v) => v.content_type === "movie" || v.content_type === "anime"), [videos]);
  const seriesShows = useMemo(() => buildSeriesShows(videos, { includeAnime: false }), [videos]);
  const animeShows = useMemo(
    () => buildSeriesShows(videos, { includeAnime: true }).filter((s) => s.items?.[0]?.content_type === "anime_series"),
    [videos]
  );

  const movieCategories = useMemo(() => {
    const map = new Map();
    for (const m of movies) {
      for (const cat of videoCategories(m)) {
        const key = categoryKey(cat);
        if (!map.has(key)) map.set(key, { key, name: cat, count: 0 });
        map.get(key).count += 1;
      }
    }
    return [...map.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "es"));
  }, [movies]);

  useEffect(() => {
    setMovieLimit(MOVIE_PAGE_SIZE);
  }, [tab, movieCategory, debouncedQ]);

  const qLower = debouncedQ.toLowerCase();
  const globalActive = debouncedQ.length >= 2;

  const filteredMovies = useMemo(() => {
    let list = movies;
    if (movieCategory) {
      list = list.filter((v) => videoCategories(v).some((c) => categoryKey(c) === movieCategory));
    }
    if (globalActive) {
      list = list.filter((v) => String(v.title || "").toLowerCase().includes(qLower));
    }
    return list;
  }, [movies, movieCategory, globalActive, qLower]);

  const loadMoreMovies = useCallback(() => {
    setMovieLimit((n) => n + MOVIE_PAGE_SIZE);
  }, []);

  const visibleMovies = useMemo(() => filteredMovies.slice(0, movieLimit), [filteredMovies, movieLimit]);
  const hasMoreMovies = movieLimit < filteredMovies.length;
  const movieSentinelRef = useInfiniteScroll(tab === "movies" && !globalActive && !selectedShow, hasMoreMovies, loadMoreMovies);

  const filteredSeries = useMemo(() => {
    if (!globalActive) return seriesShows;
    return seriesShows.filter((s) => s.title.toLowerCase().includes(qLower));
  }, [seriesShows, globalActive, qLower]);

  const filteredAnime = useMemo(() => {
    if (!globalActive) return animeShows;
    return animeShows.filter((s) => s.title.toLowerCase().includes(qLower));
  }, [animeShows, globalActive, qLower]);

  async function onPair(e) {
    e.preventDefault();
    const pin = pinInput.trim();
    if (pin.length !== 6) { setPairError("6 dígitos"); return; }
    setPairBusy(true);
    setPairError("");
    try {
      const data = await api("/api/remote/pair", { method: "POST", token, body: { pin } });
      const next = { session_id: data.session_id, device_name: data.device_name };
      setSession(next);
      saveStoredSession(next);
      setPinInput("");
      setStatusMsg(`Conectado a ${data.device_name || "TV"}`);
    } catch (err) {
      const msg = err.message || "PIN incorrecto";
      setPairError(
        msg.includes("expirado") || msg.includes("incorrecto")
          ? `${msg}. Mira el PIN actual en la TV (se renueva solo).`
          : msg
      );
    } finally {
      setPairBusy(false);
    }
  }

  function changeTab(id) {
    setTab(id);
    setSelectedShow(null);
    setMovieCategory("");
    setTabAnimKey((k) => k + 1);
  }

  function openShow(show) {
    setSelectedShow(show);
    setTabAnimKey((k) => k + 1);
  }

  const playing = tvState?.playing;
  const nowTitle = playing?.title || tvState?.detail?.title || "";

  function renderGrid(items, kind, onVideo) {
    if (!items.length) return <p className="rcx-empty">Sin resultados</p>;
    return (
      <div className={kind === "channel" ? "rcx-grid rcx-grid-channels" : "rcx-grid"}>
        {items.map((item, i) => {
          if (kind === "show") {
            return <PosterCard key={item.key} item={item} title={item.title} subtitle={`${item.seasonCount}T · ${item.count} eps`} index={i} onClick={() => openShow(item)} />;
          }
          if (kind === "channel") {
            return <PosterCard key={item.id} item={item} title={item.name} subtitle={item.group} badge="LIVE" index={i} onClick={() => sendCommand("play_iptv", { channel_id: item.id })} />;
          }
          return (
            <PosterCard key={item.id} item={item} title={item.title} subtitle={item.year || item.category} index={i} onClick={() => onVideo(item.id)} />
          );
        })}
      </div>
    );
  }

  const pageClass = `rcx-page rcx-page-enter rcx-page-${tab}`;

  return (
    <div className="rcx-app">
      <aside className="rcx-sidebar rcx-sidebar-collapsible">
        <div className="rcx-brand">
          <div className="rcx-brand-mark">RC</div>
          <span className="rcx-brand-text rcx-nav-label">Remote</span>
        </div>
        {NAV.map((n) => (
          <button key={n.id} type="button" className={`rcx-nav-btn ${tab === n.id && !selectedShow ? "is-active" : ""}`} onClick={() => changeTab(n.id)} title={n.label}>
            <span className="rcx-nav-icon"><NavSvg name={n.icon} /></span>
            <span className="rcx-nav-label">{n.label}</span>
          </button>
        ))}
      </aside>

      <div className="rcx-body">
        <header className="rcx-topbar rcx-sticky-head">
          <div>
            <h1>{selectedShow ? selectedShow.title : TITLES[tab]}</h1>
            <p className="rcx-topbar-sub">Navegas aquí · la TV solo cambia al reproducir</p>
          </div>
          <div className="rcx-topbar-actions">
            <div className={`rcx-status-pill ${tvOnline ? "is-online" : ""}`}>
              <span className="rcx-status-dot" />
              {session ? (tvOnline ? session.device_name : "TV desconectada") : "Sin TV"}
            </div>
            {session ? (
              <button type="button" className="rcx-btn rcx-btn-disconnect" onClick={onDisconnect} title="Desvincular y volver a ingresar el PIN">
                Desconectar TV
              </button>
            ) : null}
          </div>
        </header>

        {session && !tvOnline ? (
          <div className="rcx-offline-banner">
            <p>La TV no responde o perdió la conexión. Desconecta y vuelve a emparejar con el PIN que aparece en la tele.</p>
            <button type="button" className="rcx-btn rcx-btn-primary rcx-btn-sm" onClick={onDisconnect}>
              Desconectar y cambiar TV
            </button>
          </div>
        ) : null}

        {session ? (
          <div className="rcx-toolbar rcx-sticky-head">
            <div className="rcx-search-wrap">
              <NavSvg name="search" />
              <input
                className="rcx-search-global"
                type="search"
                placeholder="Buscar películas, series, anime, canales…"
                value={globalQ}
                onChange={(e) => setGlobalQ(e.target.value)}
              />
              {globalQ ? (
                <button type="button" className="rcx-search-clear" onClick={() => setGlobalQ("")}>×</button>
              ) : null}
            </div>
          </div>
        ) : null}

        <main className="rcx-main">
          {!session ? (
            <div className="rcx-panel rcx-pair-panel rcx-page-enter">
              <h2>Vincular TV</h2>
              <p>Ingresa el PIN de 6 dígitos que aparece en la tele.</p>
              <p className="rcx-pair-hint">Si el control quedó desincronizado, desconecta desde el menú superior o cierra esta pestaña y vuelve a entrar.</p>
              <form onSubmit={onPair}>
                <input className="rcx-pin-input" inputMode="numeric" maxLength={6} placeholder="000000" value={pinInput} onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 6))} />
                <button className="rcx-btn rcx-btn-primary" type="submit" disabled={pairBusy || pinInput.length !== 6}>{pairBusy ? "…" : "Conectar"}</button>
              </form>
              {pairError ? <p className="rcx-error">{pairError}</p> : null}
            </div>
          ) : null}

          {session && globalActive && !selectedShow ? (
            <div key={`search-${debouncedQ}`} className={`${pageClass} rcx-panel`}>
              <div className="rcx-panel-head"><h2>Resultados · “{debouncedQ}”</h2></div>
              {filteredMovies.length ? (<><p className="rcx-section-label">Películas</p>{renderGrid(filteredMovies.slice(0, 24), "video", (id) => sendCommand("play_video", { video_id: id }))}</>) : null}
              {filteredSeries.length ? (<><p className="rcx-section-label">Series</p>{renderGrid(filteredSeries.slice(0, 24), "show")}</>) : null}
              {filteredAnime.length ? (<><p className="rcx-section-label">Anime</p>{renderGrid(filteredAnime.slice(0, 24), "show")}</>) : null}
              {searchChannels.length ? (<><p className="rcx-section-label">Canales IPTV</p>{renderGrid(searchChannels, "channel")}</>) : null}
              {!filteredMovies.length && !filteredSeries.length && !filteredAnime.length && !searchChannels.length ? (
                <p className="rcx-empty">No hay coincidencias</p>
              ) : null}
            </div>
          ) : null}

          {session && selectedShow ? (
            <ShowControlView show={selectedShow} animKey={tabAnimKey} onBack={() => { setSelectedShow(null); setTabAnimKey((k) => k + 1); }} onPlayEpisode={(id) => sendCommand("play_video", { video_id: id })} cmdBusy={cmdBusy} />
          ) : null}

          {session && !selectedShow && !globalActive ? (
            <>
              {loading ? (
                <div className="rcx-loading rcx-page-enter"><div className="rcx-loading-bar" />Cargando catálogo…</div>
              ) : null}

              {!loading && tab === "home" ? (
                <div key={`tab-${tabAnimKey}`} className={pageClass}>
                  <div className="rcx-panel rcx-stagger" style={{ "--rcx-i": 0 }}>
                    <div className="rcx-panel-head"><h2>Continuar viendo</h2></div>
                    {recent.length ? renderGrid(recent, "video", (id) => sendCommand("play_video", { video_id: id })) : <p className="rcx-empty">Nada reciente</p>}
                  </div>
                  <div className="rcx-panel rcx-stagger" style={{ "--rcx-i": 1 }}>
                    <div className="rcx-panel-head"><h2>Destacados</h2></div>
                    {renderGrid(movies.slice(0, 12), "video", (id) => sendCommand("play_video", { video_id: id }))}
                  </div>
                </div>
              ) : null}

              {!loading && tab === "movies" ? (
                <div key={`tab-${tabAnimKey}`} className={pageClass}>
                  <div className="rcx-categories rcx-stagger" style={{ "--rcx-i": 0 }}>
                    <button type="button" className={`rcx-cat-btn ${!movieCategory ? "is-active" : ""}`} onClick={() => setMovieCategory("")}>Todas</button>
                    {movieCategories.map((c) => (
                      <button key={c.key} type="button" className={`rcx-cat-btn ${movieCategory === c.key ? "is-active" : ""}`} onClick={() => setMovieCategory(c.key)}>
                        {c.name} <em>{c.count}</em>
                      </button>
                    ))}
                  </div>
                  <div className="rcx-panel rcx-stagger" style={{ "--rcx-i": 1 }}>
                    <div className="rcx-panel-head">
                      <h2>{movieCategory ? movieCategories.find((c) => c.key === movieCategory)?.name : "Todas las películas"}</h2>
                      <span className="rcx-panel-count">{visibleMovies.length} / {filteredMovies.length}</span>
                    </div>
                    {renderGrid(visibleMovies, "video", (id) => sendCommand("play_video", { video_id: id }))}
                    {hasMoreMovies ? (
                      <div ref={movieSentinelRef} className="rcx-infinite-sentinel">
                        <div className="rcx-loading-bar" />
                        <span>Cargando más…</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {!loading && tab === "series" ? (
                <div key={`tab-${tabAnimKey}`} className={pageClass}>
                  <div className="rcx-panel">
                    <div className="rcx-panel-head"><h2>Series · {seriesShows.length}</h2></div>
                    {renderGrid(seriesShows, "show")}
                  </div>
                </div>
              ) : null}

              {!loading && tab === "anime" ? (
                <div key={`tab-${tabAnimKey}`} className={pageClass}>
                  <div className="rcx-panel">
                    <div className="rcx-panel-head"><h2>Anime · {animeShows.length}</h2></div>
                    {renderGrid(animeShows, "show")}
                  </div>
                </div>
              ) : null}

              {!loading && tab === "iptv" ? (
                <div key={`tab-${tabAnimKey}`} className={pageClass}>
                  <div className="rcx-panel">
                    <div className="rcx-panel-head"><h2>Canales en vivo</h2></div>
                    {iptvBrowse?.featured?.length ? (
                      <>{renderGrid(iptvBrowse.featured.slice(0, 12), "channel")}<p className="rcx-section-label">Todos</p></>
                    ) : null}
                    {renderGrid(iptvChannels, "channel")}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </main>
      </div>

      {session && playing ? (
        <RemoteTransport
          playing={playing}
          title={nowTitle}
          cmdBusy={cmdBusy}
          onSeek={(to) => sendCommand("seek", { to }, { silent: true })}
          onSeekDelta={(d) => sendCommand("seek", { delta: d })}
          onTogglePlay={() => sendCommand(playing.paused ? "play" : "pause")}
          onBack={() => sendCommand("back")}
          onStop={() => sendCommand("stop")}
          onSetAudio={(id) => sendCommand("set_audio", { id })}
          onSetSubtitle={(id) => sendCommand("set_subtitle", { id })}
        />
      ) : null}

      {statusMsg ? <div className="rcx-toast">{statusMsg}</div> : null}
    </div>
  );
}
